import { Location } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    OnInit,
    signal,
    WritableSignal,
} from '@angular/core';
import {
    AbstractControl,
    FormControl,
    FormControlStatus,
    FormGroup,
    FormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom, Subscription } from 'rxjs';

import { PermissionListBloc } from '@connect-services/client/blocs/permission-list.bloc';
import { RoleBloc } from '@connect-services/client/blocs/role.bloc';
import { RoleListBloc } from '@connect-services/client/blocs/role-list.bloc';
import { FormControlStatusEnum } from '@connect-services/client/models/common.model';
import { RouteCompletePathEnum } from '@connect-services/client/models/routes.model';
import { DetailPanelService, PanelAction } from '@connect-services/client/services/detail-panel.service';
import { PermissionsFormService } from '@connect-services/client/services/permissions-form.service';
import { SecurityService } from '@connect-services/client/services/security.service';
import { ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { CheckboxComponent, CheckboxState } from '@connect-services/src/lib/components/checkbox/checkbox.component';
import { InputNumberComponent } from '@connect-services/src/lib/components/input/input-number/input-number.component';
import { InputTextComponent } from '@connect-services/src/lib/components/input/text/input-text/input-text.component';
import { SelectComponent, SelectOption } from '@connect-services/src/lib/components/select/select.component';
import {
    Permission,
    PermissionControllerGet200Response,
    PermissionControllerGetRequestParams,
    PermissionsService,
    Role,
    RolesService,
} from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { FormGroupType } from '@shared/client/models/form.model';
import { defaultPaginationParameters, paginationMaxTake } from '@shared/client/models/pagination.model';
import { FormService } from '@shared/client/services/form.service';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

interface RoleEditionFormType extends FormGroupType {
    maxAssignment: FormControl<number | null>;
    name: FormControl<string | null>;
    permissions: FormControl<Permission[]>;
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CheckboxComponent,
        FormsModule,
        InputNumberComponent,
        InputTextComponent,
        RessourcesModule,
        SelectComponent,
    ],
    selector: 'app-role-edition',
    templateUrl: './role-edition.component.html',
})
export class RoleEditionComponent implements OnInit {
    public formGroup: FormGroup<RoleEditionFormType> | null = null;
    public readonly hasAllPermissions: WritableSignal<boolean> = signal(false);
    public readonly isSelfRoleEdition = computed<boolean>(
        () => this.securityService.meSignal()?.role?.id === this.role()?.id,
    );
    public readonly optionsAccess: WritableSignal<SelectOption<Permission>[]> = signal([]);
    public readonly optionsManage: WritableSignal<SelectOption<Permission>[]> = signal([]);
    public readonly permissionsFormService = inject(PermissionsFormService);
    public readonly role: WritableSignal<Role | null> = signal(null);
    public readonly selectedOptionAccess: WritableSignal<SelectOption<Permission>[]> = signal([]);
    public readonly selectedOptionManage: WritableSignal<SelectOption<Permission>[]> = signal([]);

    protected readonly CheckboxState = CheckboxState;
    protected formGroupSubscription?: Subscription;
    protected readonly translocoService = inject(TranslocoService);

    private readonly destroyRef: DestroyRef = inject(DestroyRef);
    private readonly detailPanelService = inject(DetailPanelService);
    private readonly permissionListBloc = inject(PermissionListBloc);
    private readonly permissionsService = inject(PermissionsService);
    private readonly roleBloc = inject(RoleBloc);
    private readonly roleListBloc = inject(RoleListBloc);
    private readonly rolesService: RolesService = inject(RolesService);
    private readonly securityService = inject(SecurityService);
    private readonly snackbarService = inject(SnackBarService);

    public constructor(private location: Location) {
        this.destroyRef.onDestroy(() => {
            if (this.formGroupSubscription) {
                this.formGroupSubscription.unsubscribe();
            }

            this.detailPanelService.clear();
        });

        this.detailPanelService.title.set(this.translocoService.translate('modifyRole'));
        this.detailPanelService.actions.set(this.getPanelActions());

        this.detailPanelService.navigateTo.set(RouteCompletePathEnum.USERS_ROLES);
    }

    public changeSelection(): void {
        this.hasAllPermissions.set(!this.hasAllPermissions());

        if (this.hasAllPermissions()) {
            this.selectedOptionManage.set([]);
            this.selectedOptionAccess.set([]);
        }
    }

    public maxAssignmentChange(assignment: number): void {
        if (!assignment) {
            this.formGroup?.controls.maxAssignment.setValue(null);
        }
    }

    public async ngOnInit(): Promise<void> {
        if (!this.roleBloc.value()) {
            return;
        }
        this.role.set(this.roleBloc.value());

        this.formGroup = this.initForm();

        this.formGroupSubscription = this.formGroup?.statusChanges.subscribe((status: FormControlStatus) => {
            this.detailPanelService.actions.set(this.getPanelActions(status));
        });

        await this.initPermission();
    }

    public onSelectOptionChange(permissions: Permission[], type: 'ACCES' | 'MANAGE'): void {
        const options: SelectOption<Permission>[] = permissions.reduce(
            (acc: SelectOption<Permission>[], permission: Permission) => {
                acc.push({
                    label: this.translocoService.translate(this.permissionsFormService.convertAlias(permission.alias)),
                    value: permission,
                });

                return acc;
            },
            [],
        );

        switch (type) {
            case 'ACCES':
                this.selectedOptionAccess.set(options);
                break;
            case 'MANAGE':
                this.selectedOptionManage.set(options);
                break;
        }

        this.formGroup?.controls.permissions.setValue([
            ...this.selectedOptionManage().map((option) => option.value),
            ...this.selectedOptionAccess().map((option) => option.value),
        ]);
    }

    public async submitForm(): Promise<void> {
        if (this.formGroup!.invalid) {
            return;
        }

        await this.updateRole();
        this.snackbarService.openSuccessSnackBar('modificationsSaveSuccess');
        await this.roleListBloc.load({}, { refresh: true, useLastLoadParameters: true });
        await this.roleBloc.load(
            {
                id: this.role()!.id,
            },
            { refresh: true, useLastLoadParameters: true },
        );
        await this.permissionListBloc.load(
            {
                role: this.role()!.id,
            },
            { refresh: true, useLastLoadParameters: true },
        );

        if (this.isSelfRoleEdition()) {
            await this.securityService.updateMe();
        }
    }

    private assignPermissionToOptionsList(
        permission: Permission,
        selectedOptionsSignal: WritableSignal<SelectOption<Permission>[]>,
    ): SelectOption<Permission> {
        const option = {
            label: this.translocoService.translate(this.permissionsFormService.convertAlias(permission.alias)),
            value: permission,
        };

        if (this.role()?.permissionAliases?.includes(permission.alias)) {
            selectedOptionsSignal.set([...selectedOptionsSignal(), option]);
        }

        return option;
    }

    private getAllPermissions(): Permission[] {
        return [...this.optionsManage(), ...this.optionsAccess()].map((option) => option.value);
    }

    private getPanelActions(status?: FormControlStatus): PanelAction[] {
        const isActive = status
            ? status === FormControlStatusEnum.VALID
            : !!(this.formGroup?.valid && !this.formGroup?.dirty);

        return [
            new PanelAction({
                isActive: true,
                label: this.translocoService.translate('cancel'),
                onClick: (): void => this.location.back(),
                type: ButtonType.SECONDARY,
            }),
            new PanelAction({
                icon: ['fas', 'check'],
                isActive: isActive,
                label: this.translocoService.translate('submit'),
                onClick: async (): Promise<void> => {
                    await this.submitForm();
                    this.location.back();
                },
            }),
        ];
    }

    private initForm(): FormGroup<RoleEditionFormType> {
        return new FormGroup<RoleEditionFormType>({
            maxAssignment: new FormControl<number | null>(this.role()!.maxNumberOfAssignments, {
                nonNullable: false,
                validators: [this.maxAssignmentExceedsAssignmentCountValidator()],
            }),
            name: new FormControl<string>(this.role()!.name, {
                nonNullable: true,
                validators: [
                    Validators.maxLength(FormService.VARCHAR_COMMON_LENGTH),
                    Validators.minLength(FormService.VARCHAR_MIN_LENGTH),
                    Validators.required,
                ],
            }),
            permissions: new FormControl<Permission[]>([], {
                nonNullable: true,
            }),
        });
    }

    private async initPermission(): Promise<void> {
        const rolePermissions = this.permissionListBloc.value()!.items;
        const access: SelectOption<Permission>[] = [];
        const manage: SelectOption<Permission>[] = [];

        this.role.set({
            ...this.role()!,
            permissionAliases: rolePermissions.map((permission) => permission.alias),
        });

        const allPermissions = await this.loadPermissions({
            ...defaultPaginationParameters,
            take: paginationMaxTake,
        });

        allPermissions
            .sort((permissionA, permissionB) => {
                const permissionAliasA = this.permissionsFormService.convertAlias(permissionA.alias);

                const permissionAliasB = this.permissionsFormService.convertAlias(permissionB.alias);

                return this.translocoService
                    .translate(permissionAliasA)
                    .localeCompare(this.translocoService.translate(permissionAliasB));
            })
            .forEach((permission) => {
                if (permission.alias.includes('read')) {
                    access.push(this.assignPermissionToOptionsList(permission, this.selectedOptionAccess));
                }
                if (permission.alias.includes('manage')) {
                    manage.push(this.assignPermissionToOptionsList(permission, this.selectedOptionManage));
                }
            });

        this.optionsAccess.set(access);
        this.optionsManage.set(manage);

        const rolePermissionAliases = new Set(this.role()!.permissionAliases ?? []);
        const selectablePermissions = this.getAllPermissions();

        this.hasAllPermissions.set(
            selectablePermissions.length > 0 &&
                selectablePermissions.every((permission) => rolePermissionAliases.has(permission.alias)),
        );
    }

    private async loadPermissions(paginationParameters: PermissionControllerGetRequestParams): Promise<Permission[]> {
        let page: number = 0;

        const savedItems: Permission[] = [];

        let loadedPermissions: PermissionControllerGet200Response;

        do {
            loadedPermissions = await firstValueFrom(
                this.permissionsService.permissionControllerGet({ ...paginationParameters, page: page }),
            );

            savedItems.push(...loadedPermissions.items);

            page += 1;
        } while (loadedPermissions.hasNextPage);

        return savedItems;
    }

    private maxAssignmentExceedsAssignmentCountValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value) {
                return null;
            }

            return control.value < (this.role()!.assignmentCount ?? 0) ? { assignmentExceedsValue: true } : null;
        };
    }

    private async updateRole(): Promise<void> {
        const name: string = this.formGroup!.controls.name.value ?? this.role()!.name;

        const maxNumberOfAssignments: number | null = this.formGroup!.controls.maxAssignment.value;

        const permissions: Permission[] = this.hasAllPermissions()
            ? this.getAllPermissions()
            : [
                  ...this.selectedOptionManage().map((permission) => permission.value),
                  ...this.selectedOptionAccess().map((permission) => permission.value),
              ];

        await firstValueFrom(
            this.rolesService.roleControllerPatch({
                id: this.role()!.id,
                updateRoleDto: {
                    maxNumberOfAssignments: maxNumberOfAssignments,
                    name: name,
                    permissions: permissions.map((permission) => permission.id),
                },
            }),
        );
    }
}
